import {Component, OnDestroy, OnInit} from "@angular/core";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {BehaviorSubject, Subject} from "rxjs";
import {filter, finalize, map, takeUntil, withLatestFrom} from "rxjs/operators";
import {HttpClient, HttpErrorResponse} from "@angular/common/http";
import {ActivatedRoute} from "@angular/router";
import {Dialog} from "@angular/cdk-experimental/dialog";
import {DisclaimerComponent} from "../components/disclaimer.component";

interface FormData {
    username: string
    password: string
}

@Component({
    selector: "app-login",
    templateUrl: "./login.component.html",
    styleUrls: ["./login.component.scss"]
})
export class LoginComponent implements OnInit, OnDestroy {
    private readonly login$ = new Subject<boolean>()
    public readonly submitting$ = new BehaviorSubject<boolean>(false)
    public readonly submitError$ = new BehaviorSubject<string | null>(null)
    private readonly destroyed$ = new Subject<boolean>()

    public readonly username = new FormControl(null, [Validators.required, Validators.email])
    public readonly password = new FormControl(null, Validators.required)

    public readonly form = new FormGroup({
        username: this.username,
        password: this.password
    })

    constructor(private readonly http: HttpClient,
                private readonly route: ActivatedRoute,
                private readonly dialog: Dialog) {
    }

    ngOnInit() {
        this.login$
            .pipe(takeUntil(this.destroyed$))
            .pipe(withLatestFrom(this.form.valueChanges, this.form.statusChanges))
            .pipe(filter(([_, formData, status]) => status === "VALID"))
            .subscribe(([_, formData]) => this.sendLoginRequest(formData))
    }

    public login() {
        this.login$.next(true)
    }

    private sendLoginRequest(formData: FormData) {

        const userData = {
            ...formData,
            broker: "LIQUIDITY"
        }

        this.submitting$.next(true)
        this.submitError$.next(null)

        const sourceParam$ = this.route
                                 .queryParams
                                 .pipe(map((params) => params.source))

        this.http
            .post("/tradefloor/public_api/login", userData)
            .pipe(takeUntil(this.destroyed$))
            .pipe(withLatestFrom(sourceParam$))
            .pipe(finalize(() => this.submitting$.next(false)))
            .subscribe(([_, source]) => this.redirectToApp(source),
                (errorResponse) => this.loginError(errorResponse))
    }

    private redirectToApp(source: string | null) {
        if (source == null) {
            window.location.assign("/tradefloor/#!/")
        } else {
            window.location.assign(`/tradefloor/#!/${source}`)
        }
    }

    private loginError(errorResponse: HttpErrorResponse) {
        this.submitError$.next(this.extractErrorMessage(errorResponse))
    }

    private extractErrorMessage(errorResponse: HttpErrorResponse) {
        switch (errorResponse.status) {
            case 401:
                return "Your password and email address did not match our records. Please try again"
            case 412:
                return "Your network address is not in the list of allowed addresses. Please contact your firm's IT support."
            case 503:
                return "We are performing maintenance. Please try again later."
            default:
                return "There was an error processing your request. Please try again later."
        }
    }

    public showTerms() {
        this.dialog.openFromComponent(DisclaimerComponent)
    }

    public ngOnDestroy(): void {
        this.destroyed$.next(true)
        this.destroyed$.complete()
    }
}
